import React, { FC } from 'react';

import { Button } from 'antd';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { PUBLIC_ASSETS } from '@/consts';
import { OpenOptions } from '@/config/openOptions';
import ColoredTooltip from '../../../../../Common/ColoredTooltip';
import { useStores } from '../../../../../hooks';
import { isLargeMultipartFile } from '../../../../FileAction/hooks';
import { FileActionType } from '../../../../FileAction/menuItems';
import { GET_FILE_BUTTONS } from '../../../../../MiniApps/Common';
import { ACTIONS } from '../constants';
import styles from './FileInfoButtons.module.scss';

const nameSpace = 'filesTable.modals.settings';
const infoDrawerNameSpace = `${nameSpace}.infoDrawer.buttons`;
const tooltipNameSpace = 'filesTable.contextMenu';

export interface FileInfoButtonsProps {
    handleFileAction: (action: FileActionType, fileId: string) => void;
}

const FileInfoButtons: FC<FileInfoButtonsProps> = observer(({ handleFileAction }) => {
    const { t } = useTranslation();
    const {
        filesListStore: { selectedItem },
        authSettingsStore: { HAS_API_PROXY },
    } = useStores();
    const { fid, menu, file_size: fileSize = 0 } = selectedItem || {};

    const {
        open_with: openWithFromMenu = [],
        options = [],
        share,
    } = menu || {};

    // Large multipart files can't be opened; hide "Open with" up front instead of waiting for the
    // backend to drop it after indexing (see isLargeMultipartFile).
    const openWith = isLargeMultipartFile(fileSize, HAS_API_PROXY) ? [] : openWithFromMenu;

    const availableActions = ACTIONS.filter((item) => {
        if (item.type === 'share') {
            return !!share;
        }

        if (item.type === 'download') {
            return options.includes(OpenOptions.download);
        }

        if (item.type === 'audits') {
            return options.includes('audits');
        }

        if (item.type === 'delete') {
            return options.includes('delete') || options.includes('delete_file');
        }

        return false;
    });

    return (
        <div className={styles['file-details-buttons-wrapper']}>
            {!!openWith.length && (
                <div className={styles['info-title']}>
                    {t(`${infoDrawerNameSpace}.openWith`)}
                </div>
            )}
            <div className={styles['buttons-wrapper']}>
                {openWith.map((item) => {
                    const { iconPath } = GET_FILE_BUTTONS[item];
                    const isViewer = item === 'viewer';
                    return (
                        <Button
                            data-testid={`fileInfoDrawer_openWith_${item}`}
                            onClick={() => handleFileAction?.(item, fid)}
                            className={classNames(styles.buttons, { [styles.Viewer]: isViewer })}
                            key={item}
                        >
                            <img src={`${PUBLIC_ASSETS}${iconPath}`} alt={item} />
                            {isViewer && t(`${infoDrawerNameSpace}.viewer`)}
                        </Button>
                    );
                })}
            </div>
            {!!availableActions.length && (
                <>
                    <div className={styles['info-title']}>{t(`${infoDrawerNameSpace}.more`)}</div>
                    <div className={styles['buttons-wrapper']}>
                        {availableActions.map((item) => (
                            <ColoredTooltip
                                title={t(`${tooltipNameSpace}.${item.type}`)}
                                placement="topLeft"
                                key={item.type}
                            >
                                <Button
                                    data-testid={`fileInfoDrawer_action_${item.type}`}
                                    onClick={() => handleFileAction?.(item.type as FileActionType, fid)}
                                    className={classNames(
                                        styles['other-actions'],
                                        { [styles.share]: item.type === 'share' },
                                    )}
                                >
                                    {item.icon}
                                </Button>
                            </ColoredTooltip>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
});

export default FileInfoButtons;
